/* eslint-disable no-use-before-define */
/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useState, useEffect } from 'react';
import { Card, Button, Select, Input, Space, message, DatePicker, Modal, Form, Descriptions, FormInstance } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import locale from 'antd/es/date-picker/locale/en_US';
import { getQuestionsByAssessmentUuid, addOrUpdateAssessmentRule, getAssessmentRulesByAssessmentUuid } from 'api/operation/assessment';
import type { TQuestionItem, IAssessmentRuleInput } from 'types/operation';
import { getUrlParam } from 'utils/common';
import QuestionOrderModal from '../QuestionOrderModal';
import s from './s.module.less';

const { Option } = Select;

interface ISimpleRule {
    id: string;
    questionId: string;
    answer: string | string[] | number;
    result: IResult;
    triggerTiming: 'immediate' | 'after_last_question';
    comparisonMode?: 'equals' | 'not_equals'; // 新增比较模式
}

interface IResult {
    type: 'not_suitable' | 'text_visit' | 'video_visit' | 'continue' | 'add_note';
    // 当type为continue时，可以添加跳题逻辑
    skipToQuestionId?: string; // 跳转到指定问题ID
    // 当type为add_note时，可以添加备注信息
    note?: string; // 备注信息
    selectedOptions?: string[]; // 多选选项
    customText?: string; // 自定义文本
}

interface IQuestionOrder {
    questionId: string;
    order: number;
    title: string;
}

// 根据问题数据确定输入类型
const determineInputType = (question: TQuestionItem): 'text' | 'number' | 'email' | 'select' | 'checkbox' | 'radio' | 'date' => {
    // 处理 DateQuestion 类型的问题
    if (question.type === 'DateQuestion' || question.type === 'date_question') {
        return 'date';
    }

    // 处理 ChoiceQuestion 类型的问题
    if (question.type === 'ChoiceQuestion' || question.type === 'choice_question') {
        // 如果问题有选项，根据选项数量决定是单选还是多选
        if (question.options && question.options.length > 0) {
            // 可以根据问题的其他属性来判断是多选还是单选
            // 这里简单假设有多个选项时是单选下拉框
            return 'select';
        }
        // 如果没有选项，默认为文本输入
        return 'text';
    }

    // 如果问题有选项，根据选项数量决定是单选还是多选
    if (question.options && question.options.length > 0) {
        // 可以根据问题的其他属性来判断是多选还是单选
        // 这里简单假设有多个选项时是单选下拉框
        return 'select';
    }

    // 根据问题标题或类型判断
    if (question.title?.toLowerCase().includes('email')) {
        return 'email';
    }

    if (question.title?.toLowerCase().includes('age') ||
        question.title?.toLowerCase().includes('number') ||
        question.title?.toLowerCase().includes('count')) {
        return 'number';
    }

    // 根据问题标题判断是否为日期问题
    if (question.title?.toLowerCase().includes('date') ||
        question.title?.toLowerCase().includes('birthday') ||
        question.title?.toLowerCase().includes('when')) {
        return 'date';
    }

    // 默认返回文本输入
    return 'text';
};

const SimpleRuleBuilder: React.FC = () => {
    const [questions, setQuestions] = useState<TQuestionItem[]>([]);
    const [rules, setRules] = useState<ISimpleRule[]>([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingRule, setEditingRule] = useState<ISimpleRule | null>(null);
    const [form] = Form.useForm();

    // 题目顺序相关状态
    const [questionOrder, setQuestionOrder] = useState<IQuestionOrder[]>([]);
    const [orderModalVisible, setOrderModalVisible] = useState(false);
    const [hasSetOrder, setHasSetOrder] = useState(false);

    // 总开关配置：控制是否只支持 text_visit 或 video_visit
    // 'all' 表示支持所有类型，'text_visit_only' 表示只支持 text_visit，'video_visit_only' 表示只支持 video_visit
    const [visitTypeRestriction, setVisitTypeRestriction] = useState<'all' | 'text_visit_only' | 'video_visit_only'>('all');

    const assessmentUuid = getUrlParam('uuid');
    const assessmentId = getUrlParam('id') ? Number(getUrlParam('id')) : null;

    const loadQuestions = React.useCallback(async () => {
        if (!assessmentUuid) {
            message.error('Assessment UUID not found in URL');
            return [];
        }

        setLoading(true);
        try {
            const { data } = await getQuestionsByAssessmentUuid({
                assessmentUuid,
            });

            if (data?.data) {
                // 规则只配置google form的，engagement question不进行配置
                const filteredQuestions = data.data.filter(
                    (question: TQuestionItem) =>
                        !!question.googleFormId,
                );

                setQuestions(filteredQuestions);

                // 初始化题目顺序
                const initialOrder = filteredQuestions.map(
                    (question: TQuestionItem, index: number) => ({
                        questionId: String(question.questionId),
                        order: index + 1,
                        title: question.title || `Question ${index + 1}`,
                    }),
                );
                setQuestionOrder(initialOrder);
                return initialOrder;
            }
            return [];
        } catch (error) {
            message.error('Failed to load questions');
            return [];
        } finally {
            setLoading(false);
        }
    }, [assessmentUuid]);

    // 加载已保存的规则
    const loadSavedRules = React.useCallback(async () => {
        if (!assessmentUuid) {
            return { hasQuestionOrder: false };
        }

        try {
            const { data } = await getAssessmentRulesByAssessmentUuid({
                assessmentUuid,
            });

            let hasQuestionOrder = false;

            if (data?.data) {
                // 将返回数据统一处理为数组格式（兼容单个对象或数组）
                const ruleDataArray = Array.isArray(data.data) ? data.data : [data.data];

                // 解析已保存的规则数据和题目顺序
                const savedRules = ruleDataArray.map((ruleData: Record<string, unknown>) => {
                    try {
                        const ruleConfig = JSON.parse(ruleData.assessmentRuleDataJson as string);

                        // 加载保存的题目顺序（如果存在）
                        if (ruleConfig.questionOrder && Array.isArray(ruleConfig.questionOrder)) {
                            setQuestionOrder(ruleConfig.questionOrder);
                            setHasSetOrder(true);
                            hasQuestionOrder = true;
                        }

                        // 加载保存的访问类型限制配置（如果存在）
                        if (ruleConfig.visitTypeRestriction) {
                            setVisitTypeRestriction(ruleConfig.visitTypeRestriction);
                        }

                        // 将后端数据转换为前端格式
                        return ruleConfig.rules?.map((rule: Record<string, unknown>) => {
                            const ruleQuestion = rule.question as Record<string, unknown>;
                            const ruleAnswer = rule.answer as Record<string, unknown>;
                            const ruleComparison = ruleAnswer.comparison as Record<string, unknown> | undefined;

                            return {
                                id: rule.id,
                                questionId: String(ruleQuestion.id), // 确保转换为字符串
                                answer: ruleAnswer.value,
                                result: rule.result,
                                triggerTiming: rule.triggerTiming,
                                comparisonMode: ruleComparison?.operator || 'equals',
                            };
                        }) || [];
                    } catch (error) {
                        // eslint-disable-next-line no-console
                        console.error('Error parsing rule data:', error);
                        return [];
                    }
                }).flat();

                if (savedRules.length > 0) {
                    setRules(savedRules);
                    // message.success(`Loaded ${savedRules.length} saved rules`);
                }
            }

            return { hasQuestionOrder };
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to load saved rules:', error);
            // 不显示错误消息，因为可能是第一次使用
            return { hasQuestionOrder: false };
        }
    }, [assessmentUuid]);

    useEffect(() => {
        const initializeData = async () => {
            if (assessmentUuid) {
                await loadQuestions();
                await loadSavedRules();
            }
        };
        initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assessmentUuid]);

    const addRule = () => {
        const newRule: ISimpleRule = {
            id: `rule_${Date.now()}`,
            questionId: '',
            answer: '',
            result: {
                type: 'not_suitable',
            },
            triggerTiming: 'after_last_question',
            comparisonMode: 'equals', // 默认为等于模式
        };
        setEditingRule(newRule);
        form.setFieldsValue(newRule);
        setModalVisible(true);
    };

    const updateRule = (ruleId: string, updates: Partial<ISimpleRule>) => {
        setRules(rules.map((rule) =>
            (rule.id === ruleId ? { ...rule, ...updates } : rule),
        ));
    };

    // 生成 JSON 配置的辅助函数
    const generateJsonConfig = (rulesToSave: ISimpleRule[], questionOrderToSave: IQuestionOrder[]) => {
        const finalQuestionOrder = rulesToSave.length > 0
            ? questionOrderToSave.map((item) => ({
                questionId: item.questionId,
                order: item.order,
                title: item.title,
            }))
            : [];

        return {
            assessmentUuid,
            questionOrder: finalQuestionOrder,
            visitTypeRestriction, // 保存总开关配置
            rules: rulesToSave.map((rule) => {
                const question = getQuestionById(rule.questionId);

                if (question?.title === "What's your current weight and height?") {
                    const bmiData = parseBMIAnswer(rule.answer as string);
                    return {
                        id: rule.id,
                        question: {
                            id: rule.questionId,
                            title: question?.title || '',
                            type: question?.type || 'questionFrom',
                        },
                        answer: {
                            value: rule.answer,
                            id: null,
                            comparison: {
                                operator: bmiData.operator,
                                value: parseFloat(bmiData.value) || 0,
                            },
                            note: 'bmi',
                        },
                        result: rule.result,
                        triggerTiming: rule.triggerTiming,
                    };
                }

                if (question?.type === 'DateQuestion' || question?.type === 'date_question') {
                    const dateData = parseDateAnswer(rule.answer as string);
                    return {
                        id: rule.id,
                        question: {
                            id: rule.questionId,
                            title: question?.title || '',
                            type: question?.type || 'questionFrom',
                        },
                        answer: {
                            value: rule.answer,
                            id: null,
                            comparison: {
                                operator: dateData.operator,
                                value: dateData.isAgeMode ? `${dateData.ageValue} ${dateData.ageUnit}` : dateData.value,
                                ageMode: dateData.isAgeMode || false,
                                ageValue: dateData.ageValue,
                                ageUnit: dateData.ageUnit,
                            },
                            note: dateData.isAgeMode ? 'age' : 'date',
                        },
                        result: rule.result,
                        triggerTiming: rule.triggerTiming,
                    };
                }

                let answerId = null;
                if (question?.options && Array.isArray(question.options)) {
                    if (Array.isArray(rule.answer)) {
                        answerId = rule.answer.map((answerValue) => {
                            const option = question.options?.find((opt) => getOptionValue(opt) === answerValue);
                            return option ? getOptionId(option) : null;
                        }).filter((id) => id !== null);
                    } else {
                        const option = question.options?.find((opt) => getOptionValue(opt) === rule.answer);
                        answerId = option ? getOptionId(option) : null;
                    }
                }

                const isChoiceQuestion = question?.type === 'ChoiceQuestion' || question?.type === 'choice_question';
                const hasComparisonMode = rule.comparisonMode && rule.comparisonMode !== 'equals';

                return {
                    id: rule.id,
                    question: {
                        id: rule.questionId,
                        title: question?.title || '',
                        type: question?.type || 'questionFrom',
                    },
                    answer: {
                        value: rule.answer,
                        id: answerId,
                        ...(isChoiceQuestion && hasComparisonMode && {
                            comparison: {
                                operator: rule.comparisonMode,
                            },
                        }),
                    },
                    result: {
                        type: rule.result.type,
                        ...(rule.result.skipToQuestionId && {
                            skipToQuestionId: rule.result.skipToQuestionId,
                        }),
                        ...(rule.result.selectedOptions && rule.result.selectedOptions.length > 0 && {
                            selectedOptions: rule.result.selectedOptions,
                        }),
                        ...(rule.result.customText && {
                            customText: rule.result.customText,
                        }),
                    },
                    triggerTiming: rule.triggerTiming,
                };
            }),
        };
    };

    const deleteRule = async (ruleId: string) => {
        Modal.confirm({
            className: s.deleteConfirmModal,
            title: 'Delete rule?',
            content: 'This action cannot be undone. Do you want to delete this rule?',
            okText: 'Delete',
            cancelText: 'Cancel',
            okButtonProps: { danger: true },
            onOk: async () => {
                if (!assessmentUuid || !assessmentId) {
                    message.error('Assessment UUID or ID not found');
                    return;
                }

                const newRules = rules.filter((rule) => rule.id !== ruleId);

                try {
                    const jsonConfig = generateJsonConfig(newRules, questionOrder);

                    const ruleData: IAssessmentRuleInput = {
                        assessmentId,
                        assessmentUuid,
                        assessmentRuleDataJson: JSON.stringify(jsonConfig),
                    };

                    const result = await addOrUpdateAssessmentRule(ruleData);
                    if (!result.error) {
                        setRules(newRules);
                        message.success('Rule deleted successfully');
                    } else {
                        message.error('Failed to delete rule');
                    }
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error('Error deleting rule:', error);
                    message.error('Failed to delete rule');
                }
            },
        });
    };

    // 题目顺序相关函数

    const closeOrderModal = () => {
        setOrderModalVisible(false);
    };

    const saveQuestionOrder = async (newOrder: IQuestionOrder[]) => {
        if (!assessmentUuid || !assessmentId) {
            message.error('Assessment UUID or ID not found');
            return;
        }

        try {
            // 用户手动设置题目顺序，始终保存（即使没有规则）
            const jsonConfig = generateJsonConfig(rules, newOrder);

            const ruleData: IAssessmentRuleInput = {
                assessmentId,
                assessmentUuid,
                assessmentRuleDataJson: JSON.stringify(jsonConfig),
            };

            const result = await addOrUpdateAssessmentRule(ruleData);
            if (!result.error) {
                setQuestionOrder(newOrder);
                setHasSetOrder(true);
                setOrderModalVisible(false);
                message.success('Question order saved successfully');
            } else {
                message.error('Failed to save question order');
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error saving question order:', error);
            message.error('Failed to save question order');
        }
    };

    // 检查是否有continue skip question逻辑
    const hasContinueSkipLogic = () => {
        return rules.some((rule) =>
            rule.result.type === 'continue' && rule.result.skipToQuestionId,
        );
    };

    // 验证题目顺序是否已设置
    const validateQuestionOrder = () => {
        if (hasContinueSkipLogic() && !hasSetOrder) {
            message.warning('Please set the default question order first before configuring continue skip question logic.');
            return false;
        }
        return true;
    };

    const handleModalSave = async () => {
        try {
            const values = await form.validateFields();
            // eslint-disable-next-line no-console
            console.log('Form values:', values);

            // 检查是否需要验证题目顺序
            if (values.result?.type === 'continue' && values.result?.skipToQuestionId) {
                if (!validateQuestionOrder()) {
                    return;
                }
            }

            if (editingRule) {
                // 确保questionId是字符串类型
                const updatedRule = {
                    ...editingRule,
                    ...values,
                    questionId: String(values.questionId || editingRule.questionId),
                };

                if (!assessmentUuid || !assessmentId) {
                    message.error('Assessment UUID or ID not found');
                    return;
                }

                let newRules: ISimpleRule[];
                if (editingRule.id && rules.find((r) => r.id === editingRule.id)) {
                    // 编辑现有规则
                    newRules = rules.map((rule) =>
                        (rule.id === editingRule.id ? updatedRule : rule),
                    );
                } else {
                    // 添加新规则
                    newRules = [...rules, updatedRule];
                }

                // 保存到后端
                try {
                    // 如果有规则，保存题目顺序；如果没有规则，题目顺序为空数组
                    const finalQuestionOrder = newRules.length > 0
                        ? questionOrder.map((item) => ({
                            questionId: item.questionId,
                            order: item.order,
                            title: item.title,
                        }))
                        : [];

                    const jsonConfig = {
                        assessmentUuid,
                        questionOrder: finalQuestionOrder,
                        visitTypeRestriction, // 保存总开关配置
                        rules: newRules.map((rule) => {
                            const question = getQuestionById(rule.questionId);

                            // 特殊处理BMI问题
                            if (question?.title === "What's your current weight and height?") {
                                const bmiData = parseBMIAnswer(rule.answer as string);
                                return {
                                    id: rule.id,
                                    question: {
                                        id: rule.questionId,
                                        title: question?.title || '',
                                        type: question?.type || 'questionFrom',
                                    },
                                    answer: {
                                        value: rule.answer,
                                        id: null,
                                        comparison: {
                                            operator: bmiData.operator,
                                            value: parseFloat(bmiData.value) || 0,
                                        },
                                        note: 'bmi',
                                    },
                                    result: rule.result,
                                    triggerTiming: rule.triggerTiming,
                                };
                            }

                            // 特殊处理日期问题
                            if (question?.type === 'DateQuestion' || question?.type === 'date_question') {
                                const dateData = parseDateAnswer(rule.answer as string);
                                return {
                                    id: rule.id,
                                    question: {
                                        id: rule.questionId,
                                        title: question?.title || '',
                                        type: question?.type || 'questionFrom',
                                    },
                                    answer: {
                                        value: rule.answer,
                                        id: null,
                                        comparison: {
                                            operator: dateData.operator,
                                            value: dateData.isAgeMode ? `${dateData.ageValue} ${dateData.ageUnit}` : dateData.value,
                                            ageMode: dateData.isAgeMode || false,
                                            ageValue: dateData.ageValue,
                                            ageUnit: dateData.ageUnit,
                                        },
                                        note: dateData.isAgeMode ? 'age' : 'date',
                                    },
                                    result: rule.result,
                                    triggerTiming: rule.triggerTiming,
                                };
                            }

                            // 获取答案的ID
                            let answerId = null;
                            if (question?.options && Array.isArray(question.options)) {
                                if (Array.isArray(rule.answer)) {
                                    answerId = rule.answer.map((answerValue) => {
                                        const option = question.options?.find((opt) => getOptionValue(opt) === answerValue);
                                        return option ? getOptionId(option) : null;
                                    }).filter((id) => id !== null);
                                } else {
                                    const option = question.options?.find((opt) => getOptionValue(opt) === rule.answer);
                                    answerId = option ? getOptionId(option) : null;
                                }
                            }

                            // 检查是否是 ChoiceQuestion 且有比较模式
                            const isChoiceQuestion = question?.type === 'ChoiceQuestion' || question?.type === 'choice_question';
                            const hasComparisonMode = rule.comparisonMode && rule.comparisonMode !== 'equals';

                            return {
                                id: rule.id,
                                question: {
                                    id: rule.questionId,
                                    title: question?.title || '',
                                    type: question?.type || 'questionFrom',
                                },
                                answer: {
                                    value: rule.answer,
                                    id: answerId,
                                    ...(isChoiceQuestion && hasComparisonMode && {
                                        comparison: {
                                            operator: rule.comparisonMode,
                                        },
                                    }),
                                },
                                result: {
                                    type: rule.result.type,
                                    ...(rule.result.skipToQuestionId && {
                                        skipToQuestionId: rule.result.skipToQuestionId,
                                    }),
                                    ...(rule.result.selectedOptions && rule.result.selectedOptions.length > 0 && {
                                        selectedOptions: rule.result.selectedOptions,
                                    }),
                                    ...(rule.result.customText && {
                                        customText: rule.result.customText,
                                    }),
                                },
                                triggerTiming: rule.triggerTiming,
                            };
                        }),
                    };

                    const ruleData: IAssessmentRuleInput = {
                        assessmentId,
                        assessmentUuid,
                        assessmentRuleDataJson: JSON.stringify(jsonConfig),
                    };

                    const result = await addOrUpdateAssessmentRule(ruleData);
                    if (!result.error) {
                        setRules(newRules);
                        setModalVisible(false);
                        setEditingRule(null);
                        form.resetFields();
                        message.success('Rule saved successfully');
                    } else {
                        message.error('Failed to save rule');
                    }
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error('Error saving rule:', error);
                    message.error('Failed to save rule');
                }
            }
        } catch (errorInfo) {
            // eslint-disable-next-line no-console
            console.log('Validation failed:', errorInfo);
        }
    };

    const handleModalCancel = () => {
        setModalVisible(false);
        setEditingRule(null);
        form.resetFields();
    };

    const editRule = (rule: ISimpleRule) => {
        setEditingRule(rule);
        // 确保questionId是字符串类型，与Select组件的value匹配
        const formValues = {
            ...rule,
            questionId: String(rule.questionId),
        };
        form.setFieldsValue(formValues);
        setModalVisible(true);
    };

    function getQuestionById(questionId: string | number) {
        return questions.find((q) => String(q.questionId) === String(questionId));
    }

    // 提取选项的显示文本和值
    function getOptionValue(option: string | object | number): string {
        if (typeof option === 'string') {
            return option;
        }
        if (typeof option === 'object' && option !== null) {
            const obj = option as Record<string, unknown>;
            return String(obj.value || obj.label || obj.text || obj.id || option);
        }
        return String(option);
    }

    function getOptionLabel(option: string | object | number): string {
        if (typeof option === 'string') {
            return option;
        }
        if (typeof option === 'object' && option !== null) {
            const obj = option as Record<string, unknown>;
            return String(obj.label || obj.text || obj.value || obj.id || option);
        }
        return String(option);
    }

    // 获取选项的ID
    function getOptionId(option: string | object | number): string | number | null {
        if (typeof option === 'string') {
            return null; // 字符串选项没有ID
        }
        if (typeof option === 'object' && option !== null) {
            const obj = option as Record<string, unknown>;
            return (obj.id as string | number) || null;
        }
        return null;
    }

    // 解析BMI答案格式
    function parseBMIAnswer(answer: string): { operator: string; value: string } {
        if (!answer) {
            return { operator: 'less_than', value: '' };
        }

        const match = answer.match(/^BMI\s*(<|<=|>|>=|=)\s*(\d+(?:\.\d+)?)$/);
        if (match) {
            const operatorMap: Record<string, string> = {
                '<': 'less_than',
                '<=': 'less_than_or_equal',
                '>': 'greater_than',
                '>=': 'greater_than_or_equal',
                '=': 'equal',
            };
            return {
                operator: operatorMap[match[1]] || 'less_than',
                value: match[2],
            };
        }

        const noValueMatch = answer.match(/^BMI\s*(<|<=|>|>=|=)\s*\[no_value\]$/);
        if (noValueMatch) {
            const operatorMap: Record<string, string> = {
                '<': 'less_than',
                '<=': 'less_than_or_equal',
                '>': 'greater_than',
                '>=': 'greater_than_or_equal',
                '=': 'equal',
            };
            return {
                operator: operatorMap[noValueMatch[1]] || 'less_than',
                value: '',
            };
        }

        return { operator: 'less_than', value: '' };
    }

    // 格式化BMI答案
    function formatBMIAnswer(operator: string, value: string): string {
        const operatorMap: Record<string, string> = {
            less_than: '<',
            less_than_or_equal: '<=',
            greater_than: '>',
            greater_than_or_equal: '>=',
            equal: '=',
        };

        const operatorSymbol = operatorMap[operator] || '<';

        if (!value) {
            return `BMI ${operatorSymbol} [no_value]`;
        }

        return `BMI ${operatorSymbol} ${value}`;
    }

    // 解析日期答案格式
    function parseDateAnswer(answer: string): {
        operator: string;
        value: string;
        isAgeMode?: boolean;
        ageValue?: string;
        ageUnit?: string;
    } {
        if (!answer) {
            return { operator: 'before', value: '', isAgeMode: false };
        }

        const ageMatch = answer.match(/^Age\s*(before|before_or_equal|after|after_or_equal|on)\s*(\d+)\s*(years|months|days)$/);
        if (ageMatch) {
            return {
                operator: ageMatch[1],
                value: '',
                isAgeMode: true,
                ageValue: ageMatch[2],
                ageUnit: ageMatch[3],
            };
        }

        const match = answer.match(/^Date\s*(<|<=|>|>=|=|before|after|on)\s*(\d{4}-\d{2}-\d{2})$/);
        if (match) {
            const operatorMap: Record<string, string> = {
                '<': 'before',
                '<=': 'before_or_equal',
                '>': 'after',
                '>=': 'after_or_equal',
                '=': 'on',
                before: 'before',
                after: 'after',
                on: 'on',
            };
            return {
                operator: operatorMap[match[1]] || 'before',
                value: match[2],
                isAgeMode: false,
            };
        }

        const noDateMatch = answer.match(/^Date\s*(before|before_or_equal|after|after_or_equal|on)\s*\[no_date\]$/);
        if (noDateMatch) {
            return {
                operator: noDateMatch[1],
                value: '',
                isAgeMode: false,
            };
        }

        return { operator: 'before', value: '', isAgeMode: false };
    }

    // 格式化日期答案
    function formatDateAnswer(operator: string, value: string): string {
        if (!value) {
            return `Date ${operator} [no_date]`;
        }

        const operatorMap: Record<string, string> = {
            before: '<',
            before_or_equal: '<=',
            after: '>',
            after_or_equal: '>=',
            on: '=',
        };

        const operatorSymbol = operatorMap[operator] || '<';
        return `Date ${operatorSymbol} ${value}`;
    }

    // 格式化年龄答案
    function formatAgeAnswer(operator: string, ageValue: string, ageUnit: string): string {
        if (!ageValue) {
            return `Age ${operator} 0 ${ageUnit || 'years'}`;
        }

        return `Age ${operator} ${ageValue} ${ageUnit || 'years'}`;
    }

    // BMI特殊答案输入组件
    const getBMIAnswerInput = (rule: ISimpleRule, formInstance?: FormInstance) => {
        // 解析现有的BMI答案格式
        const bmiAnswer = rule.answer as string || '';
        const bmiData = parseBMIAnswer(bmiAnswer);

        return (
            <div className={s.bmiAnswerContainer}>
                <div className={s.bmiAnswerRow}>
                    <span className={s.bmiLabel}>BMI</span>
                    <Select
                        value={bmiData.operator}
                        onChange={(value) => {
                            const newAnswer = formatBMIAnswer(value, bmiData.value);
                            if (formInstance) {
                                formInstance.setFieldValue('answer', newAnswer);
                            } else {
                                updateRule(rule.id, { answer: newAnswer });
                            }
                        }}
                        style={{ width: 220, marginRight: 8 }}
                    >
                        <Option value="less_than">Less than</Option>
                        <Option value="less_than_or_equal">Less than or equal</Option>
                        <Option value="greater_than">Greater than</Option>
                        <Option value="greater_than_or_equal">Greater than or equal</Option>
                        <Option value="equal">Equal to</Option>
                    </Select>
                    <Input
                        type="number"
                        value={bmiData.value}
                        onChange={(e) => {
                            const newAnswer = formatBMIAnswer(bmiData.operator, e.target.value);
                            if (formInstance) {
                                formInstance.setFieldValue('answer', newAnswer);
                            } else {
                                updateRule(rule.id, { answer: newAnswer });
                            }
                        }}
                        placeholder="Value"
                        style={{ width: 120 }}
                    />
                </div>
            </div>
        );
    };

    // ChoiceQuestion 特殊答案输入组件
    const getChoiceAnswerInput = (rule: ISimpleRule, question: TQuestionItem, formInstance?: FormInstance) => {
        return (
            <div className={s.choiceAnswerContainer}>
                {/* 比较模式选择和选项选择 - 同行显示 */}
                <div className={s.choiceAnswerRow} style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span className={s.choiceLabel}>Mode:</span>
                        <Select
                            value={rule.comparisonMode || 'equals'}
                            onChange={(value) => {
                                if (formInstance) {
                                    formInstance.setFieldValue('comparisonMode', value);
                                    // 在弹窗中，同时更新editingRule以确保UI同步
                                    if (editingRule) {
                                        setEditingRule({
                                            ...editingRule,
                                            comparisonMode: value,
                                        });
                                    }
                                } else {
                                    updateRule(rule.id, { comparisonMode: value });
                                }
                            }}
                            style={{ width: 120 }}
                        >
                            <Option value="equals">Equals</Option>
                            <Option value="not_equals">Not Equals</Option>
                        </Select>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                        <span className={s.choiceLabel}>Answer:</span>
                        <Select
                            value={rule.answer}
                            onChange={(value) => {
                                if (formInstance) {
                                    formInstance.setFieldValue('answer', value);
                                    // 在弹窗中，同时更新editingRule以确保UI同步
                                    if (editingRule) {
                                        setEditingRule({
                                            ...editingRule,
                                            answer: value,
                                        });
                                    }
                                } else {
                                    updateRule(rule.id, { answer: value });
                                }
                            }}
                            placeholder="Select answer"
                            style={{ width: '100%' }}
                        >
                            {question.options?.map((option, index) => (
                                <Option key={getOptionValue(option) || index} value={getOptionValue(option)}>
                                    {getOptionLabel(option)}
                                </Option>
                            ))}
                        </Select>
                    </div>
                </div>
            </div>
        );
    };

    // 日期特殊答案输入组件
    const getDateAnswerInput = (rule: ISimpleRule, formInstance?: FormInstance) => {
        // 解析现有的日期答案格式
        const dateAnswer = rule.answer as string || '';
        const dateData = parseDateAnswer(dateAnswer);
        const isAgeMode = dateData.isAgeMode || false;

        return (
            <div className={s.dateAnswerContainer}>
                {/* 模式切换 */}
                <div className={s.dateAnswerRow} style={{ marginBottom: 8 }}>
                    <span className={s.dateLabel}>Mode: </span>
                    <Select
                        value={isAgeMode ? 'age' : 'date'}
                        onChange={(value) => {
                            const newIsAgeMode = value === 'age';
                            const newAnswer = newIsAgeMode
                                ? 'Age before 18 years'
                                : 'Date before [no_date]';
                            if (formInstance) {
                                formInstance.setFieldValue('answer', newAnswer);
                            } else {
                                updateRule(rule.id, { answer: newAnswer });
                            }
                        }}
                        style={{ width: 100, marginRight: 8 }}
                    >
                        <Option value="date">Date </Option>
                        <Option value="age">Age </Option>
                    </Select>
                </div>

                {/* 年龄模式 */}
                {isAgeMode ? (
                    <div className={s.dateAnswerRow}>
                        <span className={s.dateLabel}>Age: </span>
                        <Select
                            value={dateData.operator}
                            onChange={(value) => {
                                const newAnswer = formatAgeAnswer(value, dateData.ageValue || '0', dateData.ageUnit || 'years');
                                if (formInstance) {
                                    formInstance.setFieldValue('answer', newAnswer);
                                } else {
                                    updateRule(rule.id, { answer: newAnswer });
                                }
                            }}
                            style={{ width: 180, marginRight: 8 }}
                        >
                            <Option value="before">Less than</Option>
                            <Option value="before_or_equal">Less than or equal</Option>
                            <Option value="after">Greater than</Option>
                            <Option value="after_or_equal">Greater than or equal</Option>
                            <Option value="on">Equal to</Option>
                        </Select>
                        <Input
                            type="number"
                            value={dateData.ageValue || ''}
                            onChange={(e) => {
                                const newAnswer = formatAgeAnswer(dateData.operator, e.target.value, dateData.ageUnit || 'years');
                                if (formInstance) {
                                    formInstance.setFieldValue('answer', newAnswer);
                                } else {
                                    updateRule(rule.id, { answer: newAnswer });
                                }
                            }}
                            placeholder="18"
                            style={{ width: 80, marginRight: 8 }}
                            min="0"
                        />
                        <Select
                            value={dateData.ageUnit || 'years'}
                            onChange={(value) => {
                                const newAnswer = formatAgeAnswer(dateData.operator, dateData.ageValue || '0', value);
                                if (formInstance) {
                                    formInstance.setFieldValue('answer', newAnswer);
                                } else {
                                    updateRule(rule.id, { answer: newAnswer });
                                }
                            }}
                            style={{ width: 80 }}
                        >
                            <Option value="years">Years</Option>
                            <Option value="months">Months</Option>
                            <Option value="days">Days</Option>
                        </Select>
                    </div>
                ) : (
                    /* 日期模式 */
                    <div className={s.dateAnswerRow}>
                        <span className={s.dateLabel}>Date: </span>
                        <Select
                            value={dateData.operator}
                            onChange={(value) => {
                                // 即使没有选择日期，也要保存操作符
                                const newAnswer = dateData.value ? formatDateAnswer(value, dateData.value) : `Date ${value} [no_date]`;
                                if (formInstance) {
                                    formInstance.setFieldValue('answer', newAnswer);
                                } else {
                                    updateRule(rule.id, { answer: newAnswer });
                                }
                            }}
                            style={{ width: 120, marginRight: 8 }}
                        >
                            <Option value="before">Before</Option>
                            <Option value="before_or_equal">Before or equal</Option>
                            <Option value="after">After</Option>
                            <Option value="after_or_equal">After or equal</Option>
                            <Option value="on">On</Option>
                        </Select>
                        <DatePicker
                            value={dateData.value ? dayjs(dateData.value) : null}
                            onChange={(date) => {
                                const dateString = date ? date.format('YYYY-MM-DD') : '';
                                const newAnswer = formatDateAnswer(dateData.operator, dateString);
                                if (formInstance) {
                                    formInstance.setFieldValue('answer', newAnswer);
                                } else {
                                    updateRule(rule.id, { answer: newAnswer });
                                }
                            }}
                            placeholder="Select date"
                            style={{ width: 140 }}
                            format="YYYY-MM-DD"
                            locale={locale}
                        />
                    </div>
                )}
            </div>
        );
    };

    // 弹窗中的答案输入组件，与Form组件配合
    const getModalAnswerInput = (rule: ISimpleRule, formInstance: FormInstance) => {
        const question = getQuestionById(rule.questionId);
        if (!question) {
            return (
                <div className={s.placeholder}>
                    <span>-</span>
                </div>
            );
        }

        const inputType = determineInputType(question);

        // 特殊处理：检查是否是BMI相关的问题
        if (question.title === "What's your current weight and height?") {
            return (
                <div className={s.answerInputContainer}>
                    {getBMIAnswerInput(rule, formInstance)}
                </div>
            );
        }

        // 特殊处理：检查是否是日期相关的问题
        if (question.type === 'DateQuestion' || question.type === 'date_question' || inputType === 'date') {
            return (
                <div className={s.answerInputContainer}>
                    {getDateAnswerInput(rule, formInstance)}
                </div>
            );
        }

        // 处理 ChoiceQuestion 类型的问题
        if (question.type === 'ChoiceQuestion' || question.type === 'choice_question') {
            if (question.options && question.options.length > 0) {
                return (
                    <div className={s.answerInputContainer}>
                        {getChoiceAnswerInput(rule, question, formInstance)}
                    </div>
                );
            }
            return (
                <div className={s.answerInputContainer}>
                    <Input
                        value={rule.answer}
                        onChange={(e) => formInstance.setFieldValue('answer', e.target.value)}
                        placeholder="Enter answer"
                    />
                </div>
            );
        }

        // 如果问题有选项但不是 ChoiceQuestion 类型，也尝试显示选项
        if (question.options && question.options.length > 0) {
            return (
                <div className={s.answerInputContainer}>
                    <Select
                        value={rule.answer}
                        onChange={(value) => formInstance.setFieldValue('answer', value)}
                        placeholder="Select answer"
                        style={{ width: '100%' }}
                    >
                        {question.options.map((option, index) => (
                            <Option key={getOptionValue(option) || index} value={getOptionValue(option)}>
                                {getOptionLabel(option)}
                            </Option>
                        ))}
                    </Select>
                </div>
            );
        }

        if (inputType === 'select' || inputType === 'radio') {
            return (
                <div className={s.answerInputContainer}>
                    <Select
                        value={rule.answer}
                        onChange={(value) => formInstance.setFieldValue('answer', value)}
                        placeholder="Select answer"
                        style={{ width: '100%' }}
                    >
                        {question.options?.map((option, index) => (
                            <Option key={getOptionValue(option) || index} value={getOptionValue(option)}>
                                {getOptionLabel(option)}
                            </Option>
                        ))}
                    </Select>
                </div>
            );
        }

        if (inputType === 'checkbox') {
            return (
                <div className={s.answerInputContainer}>
                    <Select
                        mode="multiple"
                        value={Array.isArray(rule.answer) ? rule.answer : [rule.answer]}
                        onChange={(value) => formInstance.setFieldValue('answer', value)}
                        placeholder="Select answers"
                        style={{ width: '100%' }}
                    >
                        {question.options?.map((option, index) => (
                            <Option key={getOptionValue(option) || index} value={getOptionValue(option)}>
                                {getOptionLabel(option)}
                            </Option>
                        ))}
                    </Select>
                </div>
            );
        }

        if (inputType === 'number') {
            return (
                <div className={s.answerInputContainer}>
                    <Input
                        type="number"
                        value={rule.answer}
                        onChange={(e) => formInstance.setFieldValue('answer', e.target.value)}
                        placeholder="Enter number"
                    />
                </div>
            );
        }

        return (
            <div className={s.answerInputContainer}>
                <Input
                    value={rule.answer}
                    onChange={(e) => form.setFieldValue('answer', e.target.value)}
                    placeholder="Enter answer"
                />
            </div>
        );
    };

    // 生成JSON预览数据
    const generateJsonPreview = () => {
        return generateJsonConfig(rules, questionOrder);
    };

    // 保存访问类型限制配置的辅助函数
    const saveVisitTypeRestriction = async (newRestriction: 'all' | 'text_visit_only' | 'video_visit_only') => {
        if (!assessmentUuid || !assessmentId) {
            return;
        }

        try {
            const jsonConfig = generateJsonConfig(rules, questionOrder);
            // 更新访问类型限制
            jsonConfig.visitTypeRestriction = newRestriction;

            const ruleData: IAssessmentRuleInput = {
                assessmentId,
                assessmentUuid,
                assessmentRuleDataJson: JSON.stringify(jsonConfig),
            };

            const result = await addOrUpdateAssessmentRule(ruleData);
            if (!result.error) {
                message.success('Visit type restriction updated successfully');
            } else {
                message.error('Failed to update visit type restriction');
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error updating visit type restriction:', error);
            message.error('Failed to update visit type restriction');
        }
    };

    if (loading) {
        return <div>Loading questions...</div>;
    }

    return (
        <div className={s.simpleRuleBuilder}>
            <div className={s.header}>
                <Space>
                    <Button type="primary" onClick={addRule}>
                        Add Rule
                    </Button>
                    {/* <Button icon={<SettingOutlined />} onClick={openOrderModal}>
                        Set Question Order
                    </Button> */}
                </Space>
            </div>

            {/* 总开关配置：访问类型限制 */}
            <div className={s.visitTypeRestrictionContainer}>
                <span className={s.visitTypeRestrictionLabel}>
                    Visit Type Restriction:
                </span>
                <Select
                    value={visitTypeRestriction}
                    onChange={async (value) => {
                        setVisitTypeRestriction(value);
                        // 当总开关改变时，自动保存配置
                        await saveVisitTypeRestriction(value);
                    }}
                    className={s.visitTypeRestrictionSelect}
                >
                    <Option value="all">All Types</Option>
                    <Option value="text_visit_only">Text Visit Only</Option>
                    <Option value="video_visit_only">Video Visit Only</Option>
                </Select>
            </div>

            <div className={s.rulesList}>
                {rules.map((rule) => {
                    const question = getQuestionById(rule.questionId);
                    // 优先显示问题标题，如果没有则显示ID
                    const questionTitle =
                        question?.title || `Question ID: ${rule.questionId}`;

                    // 格式化答案显示
                    const formatAnswerDisplay = (ruleData: ISimpleRule) => {
                        if (!ruleData.answer) return '-';

                        // 特殊处理BMI答案
                        if (
                            question?.title ===
                            "What's your current weight and height?"
                        ) {
                            const bmiData = parseBMIAnswer(
                                ruleData.answer as string
                            );
                            return `BMI ${bmiData.operator} ${
                                bmiData.value || '[no_value]'
                            }`;
                        }

                        // 特殊处理日期答案
                        if (
                            question?.type === 'DateQuestion' ||
                            question?.type === 'date_question'
                        ) {
                            const dateData = parseDateAnswer(
                                ruleData.answer as string
                            );
                            if (dateData.isAgeMode) {
                                return `Age ${dateData.operator} ${
                                    dateData.ageValue || '0'
                                } ${dateData.ageUnit || 'years'}`;
                            }
                            return `Date ${dateData.operator} ${
                                dateData.value || '[no_date]'
                            }`;
                        }

                        // ChoiceQuestion 显示比较符（Equals / Not Equals）
                        if (
                            question?.type === 'ChoiceQuestion' ||
                            question?.type === 'choice_question'
                        ) {
                            const operatorLabel =
                                ruleData.comparisonMode === 'not_equals'
                                    ? 'Not Equals'
                                    : 'Equals';

                            // 将存储的值映射为显示的label
                            const toLabel = (val: string) => {
                                const matched = question?.options?.find(
                                    (opt) => getOptionValue(opt) === val
                                );
                                return matched ? getOptionLabel(matched) : val;
                            };

                            const answerText = Array.isArray(ruleData.answer)
                                ? ruleData.answer
                                      .map((v) => toLabel(String(v)))
                                      .join(', ')
                                : toLabel(String(ruleData.answer));

                            return `${operatorLabel}: ${answerText}`;
                        }

                        // 处理数组答案（多选）
                        if (Array.isArray(ruleData.answer)) {
                            return ruleData.answer.join(', ');
                        }

                        return String(ruleData.answer);
                    };

                    // 格式化结果显示
                    const formatResultDisplay = (result: IResult) => {
                        let resultText = '';
                        switch (result.type) {
                            case 'not_suitable':
                                resultText = 'Not Suitable';
                                break;
                            case 'continue':
                                resultText = 'Continue';
                                if (result.skipToQuestionId) {
                                    const skipQuestion = getQuestionById(
                                        result.skipToQuestionId
                                    );
                                    resultText += ` (Skip to: ${
                                        skipQuestion?.title ||
                                        'Unknown Question'
                                    })`;
                                }
                                break;
                            case 'add_note':
                                resultText = 'Add Note';
                                if (
                                    result.selectedOptions &&
                                    result.selectedOptions.length > 0
                                ) {
                                    resultText += ` (${result.selectedOptions.join(
                                        ', '
                                    )})`;
                                }
                                if (result.customText) {
                                    resultText += ` - ${result.customText}`;
                                }
                                break;
                            default:
                                resultText = result.type;
                        }
                        return resultText;
                    };

                    return (
                        <Card key={rule.id} size="small" className={s.ruleCard}>
                            <Descriptions
                                colon={false}
                                title={`Rule ${rules.indexOf(rule) + 1}`}
                                layout="vertical"
                                extra={
                                    <Space>
                                        <Button
                                            icon={<EditOutlined />}
                                            onClick={() => editRule(rule)}
                                        />
                                        <Button
                                            danger
                                            icon={<DeleteOutlined />}
                                            onClick={() => deleteRule(rule.id)}
                                        />
                                    </Space>
                                }
                                column={1}
                                size="small"
                                contentStyle={{
                                    marginBottom: '8px',
                                }}
                            >
                                <Descriptions.Item label="Question">
                                    {questionTitle}
                                </Descriptions.Item>
                                <Descriptions.Item label="Answer">
                                    {formatAnswerDisplay(rule)}
                                </Descriptions.Item>
                                <Descriptions.Item label="Result">
                                    {formatResultDisplay(rule.result)}
                                </Descriptions.Item>
                                <Descriptions.Item label="Trigger Timing">
                                    {rule.triggerTiming === 'immediate'
                                        ? 'Immediate'
                                        : 'After Last Question'}
                                </Descriptions.Item>
                            </Descriptions>
                        </Card>
                    );
                })}
            </div>

            {rules.length === 0 && (
                <Card className={s.emptyState}>
                    <div className={s.emptyContent}>
                        <p>No rules configured yet.</p>
                        <p>
                            Click &quot;Add Rule&quot; to create your first
                            rule.
                        </p>
                    </div>
                </Card>
            )}

            <div className={s.jsonPreview}>
                <Card title="JSON Preview" size="small">
                    <pre className={s.jsonCode}>
                        {JSON.stringify(generateJsonPreview(), null, 2)}
                    </pre>
                </Card>
            </div>

            {/* 规则配置弹窗 */}
            <Modal
                title={
                    editingRule && rules.find((r) => r.id === editingRule.id)
                        ? 'Edit Rule'
                        : 'Add Rule'
                }
                open={modalVisible}
                onOk={handleModalSave}
                onCancel={handleModalCancel}
                width={800}
                okText="Save"
                cancelText="Cancel"
            >
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{
                        result: { type: 'not_suitable' },
                        triggerTiming: 'after_last_question',
                        comparisonMode: 'equals',
                    }}
                >
                    <Form.Item
                        label="Question"
                        name="questionId"
                        rules={[
                            {
                                required: true,
                                message: 'Please select a question',
                            },
                        ]}
                    >
                        <Select
                            placeholder="Select question"
                            showSearch
                            optionFilterProp="children"
                        >
                            {questions.map((q) => (
                                <Option
                                    key={q.questionId}
                                    value={String(q.questionId)}
                                >
                                    {q.title || `Question ${q.questionId}`}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Answer"
                        name="answer"
                        rules={[
                            {
                                required: true,
                                message: 'Please enter an answer',
                            },
                        ]}
                    >
                        <Form.Item
                            noStyle
                            shouldUpdate={(prevValues, currentValues) =>
                                prevValues.questionId !==
                                currentValues.questionId
                            }
                        >
                            {({ getFieldValue }) => {
                                const questionId = getFieldValue('questionId');
                                const question = getQuestionById(questionId);

                                if (!question) {
                                    return (
                                        <div className={s.placeholder}>
                                            <span>
                                                {questionId
                                                    ? `Question ID ${questionId} not found`
                                                    : 'Please select a question first'}
                                            </span>
                                        </div>
                                    );
                                }

                                // 创建一个临时规则对象用于getAnswerInput函数
                                const tempRule: ISimpleRule = {
                                    id: 'temp',
                                    questionId,
                                    answer: getFieldValue('answer') || '',
                                    result: { type: 'not_suitable' },
                                    triggerTiming: 'after_last_question',
                                    comparisonMode:
                                        getFieldValue('comparisonMode') ||
                                        'equals',
                                };

                                return getModalAnswerInput(tempRule, form);
                            }}
                        </Form.Item>
                    </Form.Item>

                    <Form.Item
                        label="Result"
                        name={['result', 'type']}
                        rules={[
                            {
                                required: true,
                                message: 'Please select a result type',
                            },
                        ]}
                    >
                        <Select placeholder="Select result type">
                            <Option value="not_suitable">Not Suitable</Option>
                            <Option value="continue">Continue</Option>
                            <Option value="add_note">Add Note</Option>
                        </Select>
                    </Form.Item>

                    {/* Add Note选项的多选 */}
                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) =>
                            prevValues.result?.type !==
                            currentValues.result?.type
                        }
                    >
                        {({ getFieldValue }) => {
                            const resultType = getFieldValue([
                                'result',
                                'type',
                            ]);
                            if (resultType === 'add_note') {
                                return (
                                    <Form.Item
                                        label="Select Options"
                                        name={['result', 'selectedOptions']}
                                    >
                                        <Select
                                            mode="multiple"
                                            placeholder="Select options"
                                        >
                                            <Option value="only_text_visit">
                                                Only Text Visit
                                            </Option>
                                            <Option value="only_video_visit">
                                                Only Video Visit
                                            </Option>
                                            <Option value="no_systemic_hrt">
                                                No systemic HRT
                                            </Option>
                                            <Option value="others">
                                                Others
                                            </Option>
                                        </Select>
                                    </Form.Item>
                                );
                            }
                            return null;
                        }}
                    </Form.Item>

                    {/* Continue选项的跳题逻辑 */}
                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) =>
                            prevValues.result?.type !==
                            currentValues.result?.type
                        }
                    >
                        {({ getFieldValue }) => {
                            const resultType = getFieldValue([
                                'result',
                                'type',
                            ]);
                            if (resultType === 'continue') {
                                return (
                                    <Form.Item
                                        label="Skip to Question (Optional)"
                                        name={['result', 'skipToQuestionId']}
                                    >
                                        <Select
                                            placeholder="Select question to skip to"
                                            allowClear
                                        >
                                            {questions.map((q) => (
                                                <Option
                                                    key={q.id}
                                                    value={String(q.id)}
                                                    disabled={
                                                        String(q.id) ===
                                                        String(
                                                            getFieldValue(
                                                                'questionId'
                                                            )
                                                        )
                                                    }
                                                >
                                                    {q.title ||
                                                        `Question ${q.id}`}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                );
                            }
                            return null;
                        }}
                    </Form.Item>

                    {/* Add Note选项的自定义文本输入 */}
                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) =>
                            prevValues.result?.selectedOptions !==
                            currentValues.result?.selectedOptions
                        }
                    >
                        {({ getFieldValue }) => {
                            const selectedOptions =
                                getFieldValue(['result', 'selectedOptions']) ||
                                [];
                            if (selectedOptions.includes('others')) {
                                return (
                                    <Form.Item
                                        label="Custom Text"
                                        name={['result', 'customText']}
                                    >
                                        <Input.TextArea
                                            placeholder="Enter custom text"
                                            rows={3}
                                        />
                                    </Form.Item>
                                );
                            }
                            return null;
                        }}
                    </Form.Item>

                    <Form.Item
                        label="Trigger Timing"
                        name="triggerTiming"
                        rules={[
                            {
                                required: true,
                                message: 'Please select trigger timing',
                            },
                        ]}
                    >
                        <Select placeholder="Select trigger timing">
                            <Option value="immediate">Immediate</Option>
                            <Option value="after_last_question">
                                After Last Question
                            </Option>
                        </Select>
                    </Form.Item>

                    {/* 隐藏的comparisonMode字段，用于ChoiceQuestion */}
                    <Form.Item
                        name="comparisonMode"
                        style={{ display: 'none' }}
                    >
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 题目顺序设置Modal */}
            <QuestionOrderModal
                visible={orderModalVisible}
                onCancel={closeOrderModal}
                onSave={saveQuestionOrder}
                questionOrder={questionOrder}
            />
        </div>
    );
};

export default SimpleRuleBuilder;
